import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import EmptyState from '../../../../components/Card/EmptyState';
import { Modal } from '../../../../components/Modals/Modal';
import ApplyForJobModal from '../../../../components/Modals/ModalsActions/ApplyForJobModal';
import ReportJobModal from '../../../../components/Modals/ModalsActions/ReportJobModal';
import ModalTriggerContainer from '../../../../components/Modals/ModalTriggerContainer';
import { ModalProvider } from '../../../../context/ModalContext';
import useGetSingleInvitedJob from '../../../../hooks/dashboard/jobs/invite/useGetSingleInviteJob';
import useQueryString from '../../../../hooks/ui-control/useQueryString';
import { useAppSelector, useAppDisPatch } from '../../../../redux/store';
import { invitedSingleJobsActions } from '../../../../redux/slices/invitedSingleJobSlice';
import JobInfoBody from '../../LocumJobs/JobInfo/JobInfoBody';
import JobInfoHead from '../../LocumJobs/JobInfo/JobInfoHead';
import JobInfoSkeleton from '../../../../components/Skeletons/JobInfoSkeleton';
import { useInvitedJobsSearch } from '../../../../context/InvitedJobsSearchContext';

interface InvitedJobInfoProps {
  showMobileInfo?: boolean;
}

const InvitedJobInfo: React.FC<InvitedJobInfoProps> = ({ showMobileInfo }) => {
  const [queryParams] = useQueryString();
  const jobId = queryParams.get('jobId');

  const { getSingleInvitedJob, loading } = useGetSingleInvitedJob();
  const { jobData } = useAppSelector((s) => s.invitedSingleJob);
  const dispatch = useAppDisPatch();

  // 3) External loading (e.g. search context)
  const { loading: contexting } = useInvitedJobsSearch();

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    dispatch(invitedSingleJobsActions.clearInvitedSingleJob);
    getSingleInvitedJob(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  async function allReady(): Promise<boolean> {
    const dataReady =
      !loading && !contexting && jobData != null && jobData.id === jobId;
    if (!dataReady) {
      return false;
    }
    return true;
  }

  useEffect(() => {
    setIsReady(false);
    allReady().then(setIsReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, jobData, jobId, contexting]);

  if (!jobId) {
    return (
      <EmptyState className="no-small-screen">
        Click on invited job to preview
      </EmptyState>
    );
  }

  if (!isReady) {
    return (
      <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
        <Container>
          <JobInfoSkeleton />
        </Container>
      </StickyWrapper>
    );
  }

  return (
    <StickyWrapper className={showMobileInfo ? 'showMobileInfo' : ''}>
      <Container>
        <JobInfoHead
          imgSrc="/static/gif/happyAnimal.gif"
          title={jobData?.title ?? ''}
          institutionName={jobData?.institutionName ?? ''}
          yearsOfExperience={`${jobData?.yearsOfExperience ?? ''} years+`}
          location={jobData?.location ?? ''}
          pay={jobData?.pay ?? 0}
          payInterval={jobData?.payInterval ?? ''}
          jobStartDate={jobData?.jobStartDate ?? ''}
          jobEndDate={jobData?.jobEndDate ?? ''}
          jobId={jobData?.id ?? ''}
          jobCreatorId={jobData?.jobCreatorId}
          noShare
        />

        <JobInfoBody jobDescription={jobData?.jobDescription} />

        <ButtonArea>
          <ModalProvider>
            <ModalTriggerContainer>
              <SecondaryButton disabled={loading}>Apply</SecondaryButton>
            </ModalTriggerContainer>
            <Modal>
              <ApplyForJobModal
                jobId={jobId}
                jobCreatorId={jobData?.jobCreatorId}
              />
            </Modal>
          </ModalProvider>

          <ModalProvider>
            <ModalTriggerContainer>
              <PrimaryButton className="danger">Report</PrimaryButton>
            </ModalTriggerContainer>
            <Modal>
              <ReportJobModal
                title={jobData?.title ?? ''}
                description={jobData?.institutionName ?? ''}
                jobId={jobId}
              />
            </Modal>
          </ModalProvider>
        </ButtonArea>
      </Container>
    </StickyWrapper>
  );
};

export default InvitedJobInfo;

const StickyWrapper = styled.div`
  display: flex;
  position: sticky;
  top: 160px;
  height: 80vh;
  overflow: auto;

  @media (max-width: 884px) {
    display: none;
    &.showMobileInfo {
      display: grid;
    }
  }
`;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 32px;
  display: grid;
  gap: 16px;
  height: fit-content;
  width: 100%;
  background-color: ${({ theme }) => theme.palette.white};

  @media (max-width: 540px) {
    padding: 1rem;
    border-radius: 0;
    border: none;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 2rem;
  height: fit-content;
`;
